import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SERVICE_CONSTANTS } from './service.constants';

/**
 * PWA Management Service
 * Handles app installation prompts, PWA display mode detection, and Service Worker updates
 */
@Injectable({
  providedIn: 'root'
})
export class PwaInstallService {
  // Installation prompt management
  private installPrompt: any = null;
  private installPromptAvailable = new BehaviorSubject<boolean>(false);

  // Service Worker update management
  private updateCheckInterval: number | null = null;
  private consecutiveFailures = 0;

  get installPromptAvailable$(): Observable<boolean> {
    return this.installPromptAvailable.asObservable();
  }

  initialize(): void {
    this.setupInstallPromptListener();
    this.setupAppInstalledListener();
    this.setupInstallButtons();
    this.startUpdateChecks();
  }

  cleanup(): void {
    this.stopUpdateChecks();
  }

  private setupInstallPromptListener(): void {
    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      this.installPrompt = event;
      this.installPromptAvailable.next(true);
      this.showInstallCard();
    });
  }

  private setupAppInstalledListener(): void {
    window.addEventListener('appinstalled', () => {
      this.disableInstallPrompt();
    });
  }

  async promptInstall(): Promise<string> {
    if (!this.installPrompt) {
      throw new Error('Install prompt not available');
    }

    const result = await this.installPrompt.prompt();
    console.log(`Install prompt was: ${result.outcome}`);
    this.disableInstallPrompt();
    return result.outcome;
  }

  dismissInstallPrompt(): void {
    this.disableInstallPrompt();
  }

  private disableInstallPrompt(): void {
    this.installPrompt = null;
    this.installPromptAvailable.next(false);
    this.hideInstallCard();
  }

  private showInstallCard(): void {
    const installCard = document.querySelector('#install-card') as HTMLElement;
    if (installCard) {
      // installCard.removeAttribute('hidden');
      console.log('Install card would be shown (uncomment line above to enable)');
    }
  }

  private hideInstallCard(): void {
    const installCard = document.querySelector('#install-card') as HTMLElement;
    if (installCard) {
      installCard.setAttribute('hidden', '');
    }
  }

  getDisplayMode(): string {
    const { PWA_DISPLAY_MODES } = SERVICE_CONSTANTS;

    if (document.referrer.startsWith('android-app://')) return PWA_DISPLAY_MODES.TWA;
    if (window.matchMedia('(display-mode: browser)').matches) return PWA_DISPLAY_MODES.BROWSER;
    if (window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone) return PWA_DISPLAY_MODES.STANDALONE;
    if (window.matchMedia('(display-mode: minimal-ui)').matches) return PWA_DISPLAY_MODES.MINIMAL_UI;
    if (window.matchMedia('(display-mode: fullscreen)').matches) return PWA_DISPLAY_MODES.FULLSCREEN;
    if (window.matchMedia('(display-mode: window-controls-overlay)').matches) return PWA_DISPLAY_MODES.WINDOW_CONTROLS_OVERLAY;
    return PWA_DISPLAY_MODES.UNKNOWN;
  }

  // ===== SERVICE WORKER UPDATE MANAGEMENT =====

  startUpdateChecks(): void {
    if (!this.isServiceWorkerSupported()) {
      console.warn('Service Worker not supported, skipping update checks');
      return;
    }

    this.updateCheckInterval = window.setInterval(() => {
      this.checkForUpdates();
    }, SERVICE_CONSTANTS.SW_UPDATE.CHECK_INTERVAL_MS);

    console.log('Started periodic ServiceWorker update checks');
  }

  stopUpdateChecks(): void {
    if (this.updateCheckInterval) {
      clearInterval(this.updateCheckInterval);
      this.updateCheckInterval = null;
      console.log('Stopped periodic ServiceWorker update checks');
    }
  }

  private isServiceWorkerSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }

  private async checkForUpdates(): Promise<void> {
    try {
      const registration = await navigator.serviceWorker.ready;
      console.log('Checking for ServiceWorker updates...');
      await registration.update();

      this.consecutiveFailures = 0;
      console.log('ServiceWorker update check completed successfully');
    } catch (error) {
      this.handleUpdateFailure(error);
    }
  }

  private handleUpdateFailure(error: any): void {
    this.consecutiveFailures++;
    console.warn(`ServiceWorker update check failed (attempt ${this.consecutiveFailures}):`, error);

    if (this.consecutiveFailures >= SERVICE_CONSTANTS.SW_UPDATE.MAX_FAILURES) {
      console.warn('Too many consecutive failures. Entering cooldown period.');
      this.enterCooldownPeriod();
    }
  }

  private enterCooldownPeriod(): void {
    this.stopUpdateChecks();

    setTimeout(() => {
      console.log('Restarting ServiceWorker update checks after cooldown');
      this.consecutiveFailures = 0;
      this.startUpdateChecks();
    }, SERVICE_CONSTANTS.SW_UPDATE.COOLDOWN_MS);
  }

  private setupInstallButtons(): void {
    const installButton = document.querySelector('#install-button');
    const dismissButton = document.querySelector('#dismiss-button');

    if (installButton) {
      installButton.addEventListener('click', async () => {
        try {
          await this.promptInstall();
        } catch (error) {
          console.error('Failed to prompt install:', error);
        }
      });
    }

    if (dismissButton) {
      dismissButton.addEventListener('click', () => {
        this.dismissInstallPrompt();
      });
    }
  }
}
